/**
 * This is a fluent text input which formats time for you.  Using Moment.js
 * this component will transform strings for you:
 *   12 => 12:00 am
 *   12p => 12:00 pm
 *   12:00 pm => 12:00 pm
 **/
import moment from "moment";
import * as React from "react";

import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {Errors, KAError} from "@khan/logging";

type Props = Omit<
    JSX.LibraryManagedAttributes<
        typeof TextField,
        React.ComponentProps<typeof TextField>
    >,
    "ref"
> & {
    id?: string;
    onChange?: (value: string) => void;
};

const TimeTextField = React.forwardRef<HTMLInputElement, Props>(
    (props, ref) => {
        const {value, onChange, ...otherProps} = props;

        const isMounted = useIsMounted();
        const isEditingRef = React.useRef(false);
        const [editValue, setEditValue] = React.useState(value || "");

        // Update editValue when props.value changes
        React.useEffect(() => {
            if (value !== undefined) {
                setEditValue(value || "");
            }
        }, [value]);

        // This function will take the changes to the text field,
        // ensure that they're valid times, update the state and
        // send those changes to the owner of this control.
        // The flow for the state is that we start off with
        // a value coming from props.value.  That value is
        // copied into our editValue state.
        // As the user types, the editValue is updated, but
        // the owner does not know about these changes.  Once
        // the user commits their changes (enter or focus change)
        // then we notifiy the owner of what cleaned up edit value
        // is.  At that point it is on the owner to look at that
        // edit value and decide if it should be kept.  If it
        // is kept, then the incoming props will be updated
        // reflecting that new edit value.  We then see that
        // in the useEffect above.
        const processInput = React.useCallback((): void => {
            if (isEditingRef.current) {
                return;
            }

            let newValue = "";

            if (editValue) {
                try {
                    const parsed = moment(editValue, "HH:mm A");

                    if (parsed.isValid()) {
                        newValue = parsed.format("h:mm A");
                    } else {
                        throw new KAError(
                            `invalid time value: ${editValue}`,
                            Errors.Internal,
                        );
                    }
                } catch (e: any) {
                    newValue = value || "";
                }
            }

            if (newValue !== value && onChange) {
                onChange(newValue);
            }

            setEditValue(newValue);
        }, [editValue, value, onChange]);

        // Handle cleanup on unmount
        React.useEffect(() => {
            return () => {
                // When this component is inside a modal or popup that is dismissed,
                // its "on-blur" handler won't fire. Let's anticipate that issue by
                // firing right before unmount, IF a change has been made.
                if (!isMounted() && value !== editValue) {
                    isEditingRef.current = false;
                    processInput();
                }
            };
        }, [processInput, value, editValue, isMounted]);

        const handleInputChange = React.useCallback((inputValue: string) => {
            setEditValue(inputValue);
        }, []);

        const handleKeyDown = React.useCallback(
            (e: React.KeyboardEvent) => {
                if (e.which === 13) {
                    // Enter
                    e.preventDefault();
                    processInput();
                }
            },
            [processInput],
        );

        const handleOnBlur = React.useCallback(() => {
            isEditingRef.current = false;
            processInput();
        }, [processInput]);

        const handleOnFocus = React.useCallback(() => {
            isEditingRef.current = true;
            processInput();
        }, [processInput]);

        return (
            <TextField
                {...otherProps}
                id={props.id}
                ref={ref}
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                disabled={props.disabled}
            />
        );
    },
);

TimeTextField.displayName = "TimeTextField";

export default TimeTextField;
